import { ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { ProjectService } from '../../../core/services/project.service';
import { ProjectStatusModel } from '../../../core/models/project/project-status.model';
import { AuthService } from '../../../core/services/auth.service';
import { ApplicationService } from '../../../core/services/application.service';
import { Observable, Subscription, finalize, take } from 'rxjs';
import { OnDestroy } from '@angular/core';

type ProjectApplication = ProjectStatusModel['applications'][number];
type ProjectCategory = ProjectStatusModel['applicationCategories'][number];



interface WizardStep {
    id: number;
    title: string;
}

interface ConfigurationMenuItem {
    key: 'general' | 'authentication' | 'model' | 'security' | 'test';
    name: string;
    icon: string;
}

interface TestTab {
    key: string;
    name: string;
}

@Component({
    selector: 'app-project-create',
    standalone: false,
    templateUrl: './project-create.html',
    styleUrl: './project-create.scss'
})
export class ProjectCreateComponent implements OnDestroy {

    currentStep: number = 1;
    maxVisitedStep: number = 1;
    isLoading = false;
    dashboardErrorMessage = '';

    applicationMode: 'projects' | 'new' = 'new';
    selectedCategory: ProjectCategory['key'] | 0 = 0;
    searchText: string = '';
    sortType: string = 'recommended';
    selectedApplication: ProjectApplication | null = null;

    activeConfigurationTab: ConfigurationMenuItem['key'] = 'general';
    advancedSettingsOpen: boolean = false;
    activeTestTab: string = 'text';

    testPrompt: string = 'Merhaba, kendini tanıtır mısın?';
    testResult: string = '';
    testResponseTime: string = '';
    testing: boolean = false;
    creatingProject: boolean = false;
    createProjectError = '';
    createdProjectId: string | number | null = null;
    createdProjectCount = 0;
    private readonly subscriptions = new Subscription();

    projectStatus!: ProjectStatusModel;

    project = {
        name: '',
        description: '',
        icon: '',
        color: ''
    };

    projectIcons: string[] = [
        'bi bi-kanban',
        'bi bi-folder',
        'bi bi-code-square',
        'bi bi-cloud',
        'bi bi-cpu',
        'bi bi-database',
        'bi bi-globe2',
        'bi bi-lightning-charge',
        'bi bi-box',
        'bi bi-grid'
    ];

    projectColors: string[] = [
        '#c83228',
        '#1f6feb',
        '#2f9e44',
        '#f08c00',
        '#7048e8',
        '#0c8599',
        '#495057',
        '#d6336c'
    ];

    configuration = {
        model: 'anameta-cortex',
        temperature: 0.7,
        maxTokens: 2048,
        createApiKey: true,
        ipRestriction: false,
        requestLogs: true
    };

    steps: WizardStep[] = [
        { id: 1, title: 'Limit Kontrolü' },
        { id: 2, title: 'Proje Bilgileri' },
        { id: 3, title: 'Uygulama Seçimi' },
        { id: 4, title: 'Proje Özeti' }
    ];

    myProjects: unknown[] = [];

    categories!: ProjectCategory[];

    models: string[] = [
        'anameta-cortex',
        'anameta-cortex-mini',
        'anameta-cortex-pro'
    ];

    configurationMenu: ConfigurationMenuItem[] = [
        { key: 'general', name: 'Genel Ayarlar', icon: 'bi bi-sliders' },
        { key: 'authentication', name: 'Kimlik Doğrulama', icon: 'bi bi-person-lock' },
        { key: 'model', name: 'Model Seçimi', icon: 'bi bi-cpu' },
        { key: 'security', name: 'Güvenlik', icon: 'bi bi-shield-check' },
        { key: 'test', name: 'Test', icon: 'bi bi-check2-square' }
    ];

    testTabs: TestTab[] = [
        { key: 'text', name: 'Metin' },
        { key: 'image', name: 'Görsel' },
        { key: 'code', name: 'Kod' }
    ];

    constructor(
        private router: Router,
        private projectSvc:ProjectService,
        private appSvc:ApplicationService, 
        private auth:AuthService  ,
        private readonly changeDetectorRef: ChangeDetectorRef) {

            this.refreshProject();

    }

     refreshProject():void {

         this.subscriptions.add(this.projectSvc.GetProjectStatus().subscribe({

                next: (response) => {
                if (!response || response.isError || !response.data) {
                    this.ShowLoadingError(
                        'Çalışma alanı bilgileriniz yüklenemedi. Lütfen tekrar giriş yaparak yeniden deneyin.'
                    );
                    return;
                }

                this.projectStatus =  response.data;
                this.categories = response.data.applicationCategories;
                this.dashboardErrorMessage = '';

                if (!this.creatingProject && this.currentStep < 4) {
                    this.selectedApplication = this.projectStatus.applications.find(app => app.pid === this.selectedApplication?.pid) ?? this.projectStatus.applications[0] ?? null;
                }

                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                this.ShowLoadingError(
                    'Çalışma alanına erişilirken bir sorun oluştu. Lütfen tekrar giriş yaparak yeniden deneyin.'
                );

            }
        }));

    }

    private ShowLoadingError(message: string): void {

           this.isLoading = false;
         this.dashboardErrorMessage = message;
        this.changeDetectorRef.markForCheck();
    }

    get remainingProjectLimit(): number {
        return this.projectStatus ? Math.max(this.projectStatus.maxProjectLimitsNumber - this.projectStatus.currentProjectNumber, 0) : 0;
    }

    get projectUsagePercentage(): number {
        if (!this.projectStatus || this.projectStatus.maxProjectLimitsNumber <= 0) {
            return 100;
        }

        return Math.min(( this.projectStatus.currentProjectNumber / this.projectStatus.maxProjectLimitsNumber) * 100, 100);
    }

    get limitCircleStyle(): string {
        const value = this.projectUsagePercentage;
        const color = this.remainingProjectLimit === 0 ? '#d94a4a' : '#ad4b37';

        return `conic-gradient(${color} 0% ${value}%, #e8ecf1 ${value}% 100%)`;
    }

    get filteredApplications(): ProjectApplication[] {
        let result = [...(this.projectStatus?.applications ?? [])];

        if (this.selectedCategory !== 0) {
            const category = this.selectedCategory;

            result = result.filter(x => String(x.categoryPid) === String(category)
            );
        }

        const search = this.searchText.trim().toLowerCase();

        if (search) {
            result = result.filter(x =>
                x.name.toLowerCase().includes(search) ||
                x.description.toLowerCase().includes(search) ||
                x.vendor.toLowerCase().includes(search) ||
                x.tags.some(tag => tag.toLowerCase().includes(search))
            );
        }

        if (this.sortType === 'name') {
            result.sort((a, b) => a.name.localeCompare(b.name));
        }

        return result;
    }

    get canContinueFromConfiguration(): boolean {
        return !!this.selectedApplication && this.project.name.trim().length > 0;
    }

    get canContinueFromProjectInfo(): boolean {
        return this.project.name.trim().length > 0;
    }

    get canCreateProject(): boolean {
        return this.remainingProjectLimit > 0 &&
               !!this.selectedApplication &&
               this.project.name.trim().length > 0;
    }

    nextStep(): void {
        if (this.currentStep === 1 && this.remainingProjectLimit <= 0) {
            return;
        }

        if (this.currentStep === 2 && !this.canContinueFromProjectInfo) {
            return;
        }

        if (this.currentStep === 3 && !this.selectedApplication) {
            return;
        }

        if (!this.creatingProject && this.currentStep < 3) {
            this.currentStep++;
            this.maxVisitedStep = Math.max(this.maxVisitedStep, this.currentStep);
        }
    }

    previousStep(): void {
        if (!this.creatingProject && this.currentStep > 1 && this.currentStep < 4) {
            this.currentStep--;
        }
    }

    goToStep(step: number): void {
        if (!this.creatingProject && this.currentStep < 4 && step >= 1 && step <= Math.min(this.maxVisitedStep, 3)) {
            this.currentStep = step;
        }
    }

    setApplicationMode(mode: 'projects' | 'new'): void {
        if (mode === 'projects' && this.myProjects.length === 0) {
            return;
        }

        this.applicationMode = mode;
    }

    selectCategory(categoryPid: ProjectCategory['key']): void {
        this.selectedCategory = categoryPid;
    }

    selectApplication(application: ProjectApplication): void {
        if (!this.creatingProject) { this.selectedApplication = application; }
    }

    toggleFavorite(pid:number): void {

        const exists = this.projectStatus.applications.find(app => app.pid === pid);

            if(exists?.isFavorite) {

                 this.subscriptions.add(this.appSvc.RemoveFavorite(pid).subscribe((data)=> {
                    this.refreshProject();
                }));

            } else {

             this.subscriptions.add(this.appSvc.AddFavorite(pid).subscribe((data)=> {
                    this.refreshProject();
                }));

            }

             this.changeDetectorRef.detectChanges();

    }

    setSearchText(event: Event): void {
        this.searchText = (event.target as HTMLInputElement).value;
    }

    setSortType(event: Event): void {
        this.sortType = (event.target as HTMLSelectElement).value;
    }

    selectProjectIcon(icon: string): void {
        this.project.icon = icon;
    }

    selectProjectColor(color: string): void {
        this.project.color = color;
    }

    setProjectName(event: Event): void {
        this.project.name = (event.target as HTMLInputElement).value;
    }

    setProjectDescription(event: Event): void {
        this.project.description = (event.target as HTMLTextAreaElement).value;
    }

    setModel(event: Event): void {
        this.configuration.model = (event.target as HTMLSelectElement).value;
    }

    setTemperature(event: Event): void {
        const value = Number((event.target as HTMLInputElement).value);

        if (!Number.isNaN(value)) {
            this.configuration.temperature = value;
        }
    }

    setMaxTokens(event: Event): void {
        const value = Number((event.target as HTMLInputElement).value);

        if (!Number.isNaN(value)) {
            this.configuration.maxTokens = value;
        }
    }

    setTestPrompt(event: Event): void {
        this.testPrompt = (event.target as HTMLTextAreaElement).value;
    }

    testConfiguration(): void {
        if (!this.testPrompt.trim()) {
            return;
        }

        this.testing = true;
        this.testResult = '';

        setTimeout(() => {
            this.testing = false;
            this.testResponseTime = '1.2 sn';
            this.testResult = 'Test isteği başarıyla tamamlandı. Seçilen uygulama ve yapılandırma proje oluşturmak için hazır.';
        }, 700);
    }

    goToPackages(): void {
        this.router.navigate(['/packages']);
    }

    createProject(): void {
        if (this.creatingProject || this.currentStep !== 3 || !this.canCreateProject) { return; }
        this.createProjectError = '';
   
        const request: CreateProjectRequest = {
            name: this.project.name.trim(),
            description: this.project.description.trim(),
            icon: this.project.icon,
            color: this.project.color,
            applicationPid: this.selectedApplication!.pid,
            Location:Object
        };
        this.creatingProject = true;
        let receivedResponse = false;
        try {
            this.projectSvc.CreateProject(request).pipe(
                take(1),
                finalize(() => { this.creatingProject = false; this.changeDetectorRef.markForCheck(); })
            ).subscribe({
              next: response => {
    receivedResponse = true;

    if (!response || response.isError) {
        this.showCreateProjectError(
            response?.msg || 'Proje oluşturulamadı. Lütfen tekrar deneyin.'
        );
        return;
    }

    const projectId = response.data?.projectId;

    if (
        projectId === undefined ||
        projectId === null ||
        String(projectId).trim() === ''
    ) {
        this.showCreateProjectError(
            'Sunucu proje kimliğini döndürmedi. Yeniden oluşturmadan önce Projelerim listesini kontrol edin.'
        );
        return;
    }

    this.createdProjectId = projectId;
    this.createdProjectCount = this.projectStatus.currentProjectNumber + 1;
    this.currentStep = 4;
    this.maxVisitedStep = 4;
    this.creatingProject = false;

    this.changeDetectorRef.detectChanges();
},
            });
        } catch {
            this.creatingProject = false;
            this.showCreateProjectError('Proje kayıt isteği başlatılamadı. Lütfen tekrar deneyin.');
        }
    }

    private showCreateProjectError(message: string): void {
        this.createProjectError = message;
        this.changeDetectorRef.detectChanges();
        document.getElementById('project-create-error')?.focus();
    }

    private safeResourceUrl(value: unknown): string {
        if (typeof value !== 'string' || !value.trim()) { return ''; }
        try {
            const url = new URL(value);
            return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
        } catch { return ''; }
    }

    get applicationWebsite(): string {
        const app = this.selectedApplication as (ProjectApplication & { website?: string; link?: string }) | null;
        return this.safeResourceUrl(app?.website || app?.link);
    }

    get applicationDocumentation(): string {
        const app = this.selectedApplication as (ProjectApplication & { documentationUrl?: string }) | null;
        return this.safeResourceUrl(app?.documentationUrl);
    }

    goToDashboard(): void { this.router.navigate(['/dashboard']); }
    goToProjects(): void { this.router.navigate(['/projectlist']); }
    goToCreatedProject(): void {
        if (this.createdProjectId !== null) { this.router.navigate(['/project', this.createdProjectId]); }
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }

    GoToLogin(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
}
}
